var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
import classNames from "classnames";
import { isEqual } from "lodash";
import { GALLERY_CONSTS, ProGallery } from "pro-gallery";
import React, { useContext, useMemo, useRef } from "react";
import { LayoutFixer } from "./../../bundled-dependencies/pro-gallery-layout-fixer";
import { RicosContext } from "./../../ricos-context";
import ExpandIcon from ".././icons/expand";
import { GALLERY_TYPE } from ".././types";
import { ROOT } from "../dataHooks";
import { resizeMediaUrl } from "../lib/resize-media-url";
import styles from "../statics/styles/viewer.rtlignore.scss";
import { isValidGalleryId } from "./is-valid-gallery-id";
import { useHeight } from "./use-height";
import { useItems } from "./useItems";
import { useOptions } from "./useOptions";
import { isHorizontalLayout, sampleItems } from "./utils";
const GALLERY_EVENTS = GALLERY_CONSTS.events;
const GalleryViewer = React.memo(
  ({
    componentData,
    nodeId,
    settings,
    relValue,
    anchorTarget,
    seoMode,
    helpers,
    itemOverlayElement
  }) => {
    var _b;
    const _a = settings, { scrollingElement } = _a, gallerySettings = __objRest(_a, ["scrollingElement"]);
    const galleryRef = useRef(null);
    const {
      items: ricosItems,
      options: ricosOptions,
      disableExpand,
      disableDownload
    } = componentData || {};
    const { tabIndex } = useContext(RicosContext);
    const items = useItems(ricosItems || [], relValue, anchorTarget);
    const { options, calculatedOptions } = useOptions(ricosOptions || {}, disableDownload);
    const galleryOptions = __spreadProps(__spreadValues({}, options), {
      behaviourParams_item_overlay_backgroundColor: "transparent"
    });
    const layout = ((_b = ricosOptions == null ? void 0 : ricosOptions.layout) == null ? void 0 : _b.type) || "";
    const {
      setHeight,
      container,
      setContainerRef,
      containerStyle,
      containerClassName,
      isMeasuredContainer
    } = useHeight({
      options: galleryOptions,
      layout
    });
    const handleContextMenu = (e) => {
      return disableDownload && e.preventDefault();
    };
    const viewMode = seoMode ? GALLERY_CONSTS.viewMode.SEO : GALLERY_CONSTS.viewMode.SITE;
    const hasExpand = !disableExpand && !settings.disableExpand && settings.onExpand && ricosItems.length > 0;
    const handleExpand = (itemId) => {
      var _a2;
      (_a2 = helpers == null ? void 0 : helpers.onViewerAction) == null ? void 0 : _a2.call(helpers, GALLERY_TYPE, "Click", "expand_gallery");
      hasExpand && settings.onExpand(nodeId, itemId);
    };
    const handleGalleryEvents = (name, data) => {
      switch (name) {
        case GALLERY_EVENTS.GALLERY_CHANGE:
          if (galleryRef.current) {
            if (!isHorizontalLayout(options)) {
              galleryRef.current.style.height = `${data.layoutHeight}px`;
            } else {
              galleryRef.current.style.height = "auto";
            }
          }
          break;
        case GALLERY_EVENTS.ITEM_ACTION_TRIGGERED:
          data.type !== "video" && !data.linkData.url && !disableExpand && handleExpand(data.idx);
          break;
        default:
          break;
      }
    };
    const customHoverRenderer = (itemProps) => {
      return /* @__PURE__ */ React.createElement("div", { className: styles.itemOverlay }, itemProps.title && /* @__PURE__ */ React.createElement("div", { className: styles.imageTitleContainer }, /* @__PURE__ */ React.createElement("div", { className: styles.imageTitle }, itemProps.title)), itemOverlayElement == null ? void 0 : itemOverlayElement(itemProps));
    };
    const isPrerenderMode = !isMeasuredContainer;
    const validNodeId = useMemo(() => isValidGalleryId(nodeId) ? nodeId : "", [nodeId]);
    const customImageRenderer = (itemProps) => {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("img", __spreadProps(__spreadValues({ alt: itemProps.alt }, itemProps), { tabIndex })), hasExpand && /* @__PURE__ */ React.createElement("div", { className: styles.expandContainer, "data-hook": "expand-container" }, /* @__PURE__ */ React.createElement(
        ExpandIcon,
        {
          className: styles.expandIcon,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleExpand(itemProps["data-idx"]);
          }
        }
      )));
    };
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: setContainerRef,
        className: classNames(styles.gallery_container, containerClassName, {
          [styles.clickableItem]: hasExpand
        }),
        style: containerStyle,
        "data-hook": ROOT,
        role: "none",
        onContextMenu: handleContextMenu
      },
      /* @__PURE__ */ React.createElement(
        ProGallery,
        {
          id: nodeId,
          items: items.length > 0 ? items : sampleItems,
          options: galleryOptions,
          container,
          settings: __spreadProps(__spreadValues({}, gallerySettings), { isAccessible: true }),
          scrollingElement: typeof scrollingElement === "function" ? scrollingElement() : scrollingElement,
          eventsListener: handleGalleryEvents,
          createMediaUrl: resizeMediaUrl,
          viewMode,
          customComponents: { customHoverRenderer, customImageRenderer },
          totalItemsCount: items.length || sampleItems.length,
          setLayoutHeightImp: setHeight,
          isPrerenderMode
        }
      ),
      validNodeId ? /* @__PURE__ */ React.createElement(
        LayoutFixer,
        {
          id: validNodeId,
          isPrerenderMode,
          items,
          options: calculatedOptions
        }
      ) : null
    );
  },
  (prevProps, nextProps) => {
    return isEqual(filterOutNonDisplayedProps(prevProps), filterOutNonDisplayedProps(nextProps));
  }
);
function filterOutNonDisplayedProps(props) {
  return __spreadProps(__spreadValues({}, props), {
    componentData: __spreadProps(__spreadValues({}, props.componentData), {
      items: props.componentData.items.map(
        (_a) => {
          var _b = _a, { altText: _altText, title: _title, image } = _b, rest = __objRest(_b, ["altText", "title", "image"]);
          if (!image) {
            return rest;
          }
          const _a2 = image, { link: _link } = _a2, imageRest = __objRest(_a2, ["link"]);
          return __spreadProps(__spreadValues({}, rest), { image: imageRest });
        }
      )
    })
  });
}
export {
  GalleryViewer
};
//# sourceMappingURL=gallery-viewer.js.map
